import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight } from "lucide-react";
import { IMAGES, SITE } from "../../lib/data";

const LINES = [
  { text: "Commercial Contracting &", accent: false },
  { text: "Facility Maintenance", accent: false },
  { text: "for Petroleum Retail", accent: true },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative isolate flex min-h-[92vh] items-end overflow-hidden"
    >
      <motion.div style={{ y }} className="absolute inset-0 -z-10">
        <motion.img
          src={IMAGES.hero.url}
          alt={IMAGES.hero.alt}
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
          className="h-[115%] w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 -z-10 bg-black/35" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-black/60 via-black/25 to-transparent" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-ink via-transparent to-black/30" />
      <div className="grain-overlay pointer-events-none absolute inset-0 -z-10" />

      <motion.div style={{ opacity }} className="mx-auto w-full max-w-7xl px-5 pb-20 pt-40 lg:px-8">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.35em] text-white/60"
        >
          <span className="h-px w-10 bg-ember" />
          Milton, ON — Serving Southern Ontario
        </motion.p>

        <h1 className="font-display text-4xl font-extrabold leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                className={`block ${line.accent ? "text-ember" : "text-[#F7F7F7]"}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7 max-w-xl text-base leading-relaxed text-white/70 md:text-lg"
        >
          Reliable repair, renovation, maintenance and facility-support services for gas
          stations, convenience stores, car washes and petroleum retail facilities across Ontario.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <Link
            to="/contact"
            data-testid="hero-request-service-btn"
            className="group inline-flex items-center justify-center gap-3 bg-ember px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-coal transition-transform duration-200 hover:-translate-y-0.5"
          >
            Request Service
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
          <a
            href={SITE.phoneHref}
            data-testid="hero-call-btn"
            className="inline-flex items-center justify-center gap-3 border border-white/25 bg-black/30 px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-white backdrop-blur-sm transition-colors duration-200 hover:border-ember hover:text-ember"
          >
            <Phone className="h-4 w-4" />
            Call {SITE.phone}
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
